import { useContext } from 'react'
import { Link } from 'react-router-dom'
import type { Issue } from '../api/types'
import { Working } from '../components/Working'
import { CheckIcon, CommitIcon } from '../components/Icons'
import { pullsOf, releasedOf, releasedText } from '../lib/releases'
import { proposalPath } from '../queue/words'
import { useBoardState } from '../store/context'
import { actorName } from '../vocab'
import { BoardInteractionContext } from './interaction'
import type { Live } from './live'

/**
 * Today's fixed-height line and one action. Quiet cards draw no line, unless their work shipped: an issue imported from
 * today's board says its last pull request's number and the versions that released it, `#155 · app 0.1.2 · server 0.1.1`,
 * or either alone, wherever the line has no action of its own and no agent is at work (Poulpe-205, Poulpe-500).
 * There too, at its right end, the commit its work landed as links to its page on GitHub (Poulpe-741).
 */
export function LiveLine({ live, issue, confirmed, commit = null }: { live: Live; issue: Issue; confirmed: boolean; commit?: { url: string; sha: string } | null }) {
  const board = useContext(BoardInteractionContext)
  const { projects } = useBoardState()
  const said = live.kind === 'quiet' || live.kind === 'stuck'
  const released = said ? releasedOf(issue) : []
  const pulls = said ? pullsOf(issue) : []
  const pull = pulls[pulls.length - 1]
  const landed = said ? commit : null
  if (live.kind === 'quiet' && released.length === 0 && !pull && !landed) return null
  const working = live.kind === 'working' ? live : null
  const name = working ? actorName({ kind: 'agent', agent: working.session.agent, session: working.session.id }) : ''
  const vendor = working?.session.agent.vendor
  const engine = vendor === 'anthropic' ? 'claude' : vendor === 'openai' ? 'codex' : vendor === 'local' ? 'local' : undefined
  const text = working ? `${name} is working` : live.kind === 'needs' ? 'Needs you' : live.kind === 'stuck' ? live.why : ''
  const path = live.kind === 'needs' ? `/${encodeURIComponent(issue.key)}`
    : live.kind === 'approve' ? proposalPath(live.proposal, projects.get(issue.project)?.key) : null
  const versions = releasedText(released)
  return <div className="card-live" data-kind={live.kind}>
    {working && <Working label={text} aria-hidden />}
    {live.kind === 'quiet' && released.length > 0 && <CheckIcon size={14} />}
    {text && <span className="card-live-text" title={text}>{working ? <><span data-engine={engine}>{name}</span> is working</> : text}</span>}
    {working && working.workers > 0 && <span className="card-live-workers" title={working.workers === 1 ? '1 worker' : `${working.workers} workers`}>+{working.workers}</span>}
    {path && <Link className="card-live-action" to={path}
      onKeyDown={event => { if (event.key === ' ') { event.preventDefault(); event.currentTarget.click() } }}
      onClick={event => {
        if (live.kind !== 'needs' || !board || !confirmed || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault(); board.enlarge(issue.id)
      }}>{live.kind === 'approve' ? 'Review' : 'Answer'}</Link>}
    {!path && (pull || versions) && <span className="card-live-end">
      {pull && <a className="card-live-action" href={pull.url} target="_blank" rel="noreferrer" aria-label={`Pull request ${pull.number} on GitHub`}>#{pull.number}</a>}
      {versions && <span className="card-live-versions" title={versions}>{pull ? '· ' : ''}{versions}</span>}
    </span>}
    {!path && landed && <a className="card-live-action card-commit" href={landed.url} target="_blank" rel="noreferrer"
      aria-label={`Landed commit ${landed.sha.slice(0, 7)} on GitHub`} title={`Landed as ${landed.sha}`}><CommitIcon size={12} />{landed.sha.slice(0, 7)}</a>}
  </div>
}
